import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Star, Plus, PencilSimple, TrashSimple, ArrowSquareOut, FloppyDisk, X, User, Users } from "@phosphor-icons/react";
import ConfirmDialog from "../components/ConfirmDialog";

const EMPTY = { label: "", url: "", icon: "", category: "" };

const VIEWS = [
  { key: "personal", label: "Mes favoris",   icon: User,  testid: "fav-view-personal" },
  { key: "common",   label: "Commun équipe", icon: Users, testid: "fav-view-common" },
];

export default function Favorites() {
  const { user } = useAuth();
  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem("mt.favorites.scope") || "personal"; } catch { return "personal"; }
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const switchScope = (s) => {
    setScope(s);
    try { localStorage.setItem("mt.favorites.scope", s); } catch { /* noop */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/favorites", { params: { scope } });
      setItems(r.data || []);
    } catch { toast.error("Impossible de charger les favoris"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [scope]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (f) => { setForm({ label: f.label, url: f.url, icon: f.icon || "", category: f.category || "" }); setEditingId(f.id); setShowForm(true); };
  const cancel = () => { setShowForm(false); setForm(EMPTY); setEditingId(null); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.url.trim()) return toast.error("Libellé et URL requis");
    try {
      if (editingId) {
        await api.patch(`/favorites/${editingId}`, form);
        toast.success("Favori mis à jour");
      } else {
        await api.post("/favorites", { ...form, scope });
        toast.success(scope === "common" ? "Favori commun ajouté" : "Favori ajouté");
      }
      cancel(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/favorites/${id}`);
      toast.success("Favori supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setConfirmDel(null); }
  };

  const toggleScope = async (f) => {
    const nextScope = (f.scope || "personal") === "personal" ? "common" : "personal";
    const label = nextScope === "common" ? "Basculer ce favori en Commun équipe ?" : "Repasser ce favori en Personnel ?";
    if (!window.confirm(label)) return;
    try {
      await api.patch(`/favorites/${f.id}`, { scope: nextScope });
      toast.success(nextScope === "common" ? "Favori déplacé en Commun équipe" : "Favori repassé en Personnel");
      // Switch view to the destination so the user sees where it went
      switchScope(nextScope);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const grouped = items.reduce((acc, f) => {
    const k = (f.category || "Général").trim() || "Général";
    (acc[k] = acc[k] || []).push(f);
    return acc;
  }, {});
  const groupKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b, "fr"));

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps">Espace technique</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
            <Star weight="fill" className="text-amber-500" size={32}/> Favoris
          </h1>
          <p className="text-muted-foreground mt-2">
            {scope === "common"
              ? "Liens partagés visibles par toute l'équipe support."
              : "Liens personnels visibles uniquement par vous."}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex bg-muted/40 border rounded-lg p-1 gap-1" role="tablist" data-testid="fav-view-switcher">
            {VIEWS.map((v) => {
              const Icon = v.icon;
              const active = scope === v.key;
              return (
                <button key={v.key} onClick={() => switchScope(v.key)}
                  data-testid={v.testid} role="tab" aria-selected={active}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    active ? "bg-background shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                  }`}>
                  <Icon size={15} weight={active ? "fill" : "regular"}/>
                  {v.label}
                </button>
              );
            })}
          </div>
          {!showForm && (
            <Button onClick={openNew} data-testid="fav-add-btn">
              <Plus size={16} className="mr-2"/> Ajouter
            </Button>
          )}
        </div>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Libellé *</Label>
                  <Input data-testid="fav-label" required value={form.label}
                    onChange={(e) => setForm({...form, label: e.target.value})}
                    placeholder="Portail admin M365"/></div>
                <div><Label>URL *</Label>
                  <Input data-testid="fav-url" required value={form.url}
                    onChange={(e) => setForm({...form, url: e.target.value})}
                    placeholder="https://admin.microsoft.com"/></div>
                <div><Label>Catégorie (optionnel)</Label>
                  <Input data-testid="fav-category" value={form.category}
                    onChange={(e) => setForm({...form, category: e.target.value})}
                    placeholder="Microsoft 365, Réseau, Outils…"/></div>
                <div><Label>Icône émoji (optionnel)</Label>
                  <Input data-testid="fav-icon" value={form.icon} maxLength={4}
                    onChange={(e) => setForm({...form, icon: e.target.value})}
                    placeholder="🌐"/></div>
              </div>
              <div className="text-xs text-muted-foreground">
                {editingId ? "Modification en cours" : `Sera ajouté dans « ${scope === "common" ? "Commun équipe" : "Mes favoris"} ».`}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={cancel} data-testid="fav-cancel">
                  <X size={16} className="mr-2"/> Annuler
                </Button>
                <Button type="submit" data-testid="fav-save">
                  <FloppyDisk size={16} className="mr-2"/> {editingId ? "Mettre à jour" : "Enregistrer"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-muted-foreground">Chargement…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Star size={40} className="text-muted-foreground mx-auto mb-3"/>
            <p className="text-muted-foreground">
              {scope === "common" ? "Aucun favori partagé pour le moment." : "Aucun favori personnel pour le moment."}
            </p>
            <p className="text-sm text-muted-foreground mt-1">Cliquez sur « Ajouter » pour créer votre premier lien.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupKeys.map((cat) => (
            <div key={cat}>
              <h2 className="label-caps mb-3">{cat}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped[cat].map((f) => (
                  <Card key={f.id} data-testid={`fav-card-${f.id}`} className="group hover:border-primary/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl leading-none pt-1 shrink-0">{f.icon || "🔗"}</div>
                        <div className="min-w-0 flex-1">
                          <a href={f.url} target="_blank" rel="noopener noreferrer"
                             className="font-medium hover:text-primary flex items-center gap-1.5 truncate"
                             data-testid={`fav-link-${f.id}`}>
                            {f.label}
                            <ArrowSquareOut size={14} className="shrink-0 opacity-60"/>
                          </a>
                          <div className="text-xs text-muted-foreground truncate mt-0.5">{f.url}</div>
                          {scope === "common" && (
                            <div className="text-[0.65rem] text-muted-foreground/80 mt-1 flex items-center gap-1">
                              <User size={10}/> {f.user_id === user?.id ? "Moi" : (f.owner_name || "Inconnu")}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost"
                          title={(f.scope || "personal") === "personal" ? "Passer en commun équipe" : "Repasser en personnel"}
                          onClick={() => toggleScope(f)} data-testid={`fav-scope-${f.id}`}>
                          {(f.scope || "personal") === "personal" ? <Users size={14}/> : <User size={14}/>}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(f)} data-testid={`fav-edit-${f.id}`}>
                          <PencilSimple size={14}/>
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDel(f)} data-testid={`fav-delete-${f.id}`}>
                          <TrashSimple size={14} className="text-red-500"/>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        title="Supprimer ce favori ?"
        description={confirmDel ? `« ${confirmDel.label} » sera définitivement supprimé.` : ""}
        confirmLabel="Supprimer"
        onConfirm={() => confirmDel && remove(confirmDel.id)}
        danger
      />
    </div>
  );
}
